import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

const FONT_LINK = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Manrope:wght@300;400;500;600;700&display=swap";

const SPLITTER_CSS = `
  .splitter-root { display: flex; flex-direction: column; }
  .splitter-pane { width: 100% !important; height: 50% !important; }
  .splitter-divider-v { display: none; }
  .splitter-divider-h { display: block; }

  @media (min-width: 768px) {
    .splitter-root { flex-direction: row; }
    .splitter-pane { height: 100% !important; width: 50% !important; }
    .splitter-divider-v { display: block; }
    .splitter-divider-h { display: none; }
  }
`;

export default function HomePage() {
  const nav = useNavigate();
  const [hovered, setHovered] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setLoaded(true));
  }, []);

  return (
    <div className="w-screen overflow-hidden relative splitter-root" style={{ height: '100dvh' }} data-testid="home-splitter">
      <link href={FONT_LINK} rel="stylesheet" />
      <style>{SPLITTER_CSS}</style>
      {/* Dynamic width for desktop hover - injected as CSS */}
      <style>{`
        @media (min-width: 768px) {
          .splitter-pane-restaurant { width: ${hovered === 'restaurant' ? '65%' : hovered === 'traiteur' ? '35%' : '50%'} !important; transition: width 700ms ease-in-out, opacity 1s ease-out 0.2s !important; }
          .splitter-pane-traiteur { width: ${hovered === 'traiteur' ? '65%' : hovered === 'restaurant' ? '35%' : '50%'} !important; transition: width 700ms ease-in-out, opacity 1s ease-out 0.3s !important; }
        }
      `}</style>

      {/* ═══ RESTAURANT ═══ */}
      <div
        className="relative cursor-pointer overflow-hidden splitter-pane splitter-pane-restaurant"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 1s ease-out 0.2s' }}
        onMouseEnter={() => setHovered('restaurant')}
        onMouseLeave={() => setHovered(null)}
        onClick={() => nav('/site/restaurant')}
        data-testid="splitter-restaurant"
      >
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
          style={{ backgroundImage: 'url(/images/restaurant/home_intro.jpg)', transform: hovered === 'restaurant' ? 'scale(1.05)' : 'scale(1)' }} />
        <div className="absolute inset-0 transition-colors duration-500"
          style={{ backgroundColor: hovered === 'restaurant' ? 'rgba(26,39,68,0.6)' : 'rgba(26,39,68,0.75)' }} />

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-6">
          <img src="/images/logo-restaurant.jpg" alt="Le Mas Restaurant"
            className="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full shadow-2xl mb-2 sm:mb-4 md:mb-6 border-2 border-white/20"
            style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.8s ease-out 0.6s' }} />

          <h2 style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(15px)', transition: 'opacity 0.8s ease-out 0.8s, transform 0.8s ease-out 0.8s' }}
            className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-wide mb-1 md:mb-3">
            Restaurant
          </h2>

          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'scaleX(1)' : 'scaleX(0)', transition: 'opacity 0.6s ease-out 1s, transform 0.6s ease-out 1s' }}
            className="w-8 sm:w-12 md:w-16 h-px bg-white/40 mb-1.5 md:mb-4" />

          <p style={{ opacity: loaded ? 0.6 : 0, transition: 'opacity 0.8s ease-out 1.2s' }}
            className="font-['Manrope',sans-serif] text-[10px] sm:text-xs md:text-sm text-white/60 text-center max-w-xs">
            Cuisine mediterraneenne et locale
          </p>

          <div className={`mt-3 sm:mt-5 md:mt-8 transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '1.4s' }}>
            <span className="font-['Manrope',sans-serif] text-[9px] sm:text-[10px] md:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase text-white/90 border border-white/40 px-5 py-1.5 sm:px-6 sm:py-2 md:px-8 md:py-3 rounded-full active:bg-white/20 hover:bg-white/15 transition-colors">
              Entrer
            </span>
          </div>
        </div>
      </div>

      {/* Vertical divider (desktop) */}
      <div className="relative z-20 w-0 splitter-divider-v">
        <div className="absolute top-0 bottom-0 left-0 w-px bg-white/20"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'scaleY(1)' : 'scaleY(0)', transformOrigin: 'center', transition: 'opacity 0.8s ease-out 0.5s, transform 0.8s ease-out 0.5s' }} />
      </div>
      {/* Horizontal divider (mobile) */}
      <div className="relative z-20 h-0 splitter-divider-h">
        <div className="absolute left-[20%] right-[20%] top-0 h-px bg-white/25"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'center', transition: 'opacity 0.8s ease-out 0.5s, transform 0.8s ease-out 0.5s' }} />
      </div>

      {/* ═══ TRAITEUR ═══ */}
      <div
        className="relative cursor-pointer overflow-hidden splitter-pane splitter-pane-traiteur"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 1s ease-out 0.3s' }}
        onMouseEnter={() => setHovered('traiteur')}
        onMouseLeave={() => setHovered(null)}
        onClick={() => nav('/site/traiteur')}
        data-testid="splitter-traiteur"
      >
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
          style={{ backgroundImage: 'url(/images/traiteur/mariage.jpg)', transform: hovered === 'traiteur' ? 'scale(1.05)' : 'scale(1)' }} />
        <div className="absolute inset-0 transition-colors duration-500"
          style={{ backgroundColor: hovered === 'traiteur' ? 'rgba(13,148,136,0.55)' : 'rgba(13,148,136,0.70)' }} />

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-6">
          <img src="/images/traiteur-logo-macaron.png" alt="Le Mas Traiteur"
            className="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full shadow-2xl mb-2 sm:mb-4 md:mb-6 border-2 border-white/20 object-cover bg-white/10"
            style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.8s ease-out 0.7s' }} />

          <h2 style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(15px)', transition: 'opacity 0.8s ease-out 0.9s, transform 0.8s ease-out 0.9s' }}
            className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-wide mb-1 md:mb-3">
            Traiteur
          </h2>

          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'scaleX(1)' : 'scaleX(0)', transition: 'opacity 0.6s ease-out 1.1s, transform 0.6s ease-out 1.1s' }}
            className="w-8 sm:w-12 md:w-16 h-px bg-white/40 mb-1.5 md:mb-4" />

          <p style={{ opacity: loaded ? 0.6 : 0, transition: 'opacity 0.8s ease-out 1.3s' }}
            className="font-['Manrope',sans-serif] text-[10px] sm:text-xs md:text-sm text-white/60 text-center max-w-xs">
            Evenements, mariages et receptions
          </p>

          <div className={`mt-3 sm:mt-5 md:mt-8 transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '1.5s' }}>
            <span className="font-['Manrope',sans-serif] text-[9px] sm:text-[10px] md:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase text-white/90 border border-white/40 px-5 py-1.5 sm:px-6 sm:py-2 md:px-8 md:py-3 rounded-full active:bg-white/20 hover:bg-white/15 transition-colors">
              Entrer
            </span>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-center py-2 md:py-4"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.8s ease-out 1.6s' }}>
        <p className="font-['Manrope',sans-serif] text-[8px] md:text-[10px] text-white/30 tracking-widest uppercase">
          Saint-Laurent-de-la-Salanque · 04 68 38 06 98
        </p>
      </div>
    </div>
  );
}
